import { Application } from 'pixi.js';

export type BoardApp = {
  app: Application;
  destroy: () => void;
  resize: (width: number, height: number) => void;
  setInteractive: (interactive: boolean) => void;
};

export type CreateBoardAppOptions = {
  maxResolution?: number;
  antialias?: boolean;
  /** Prefer battery-friendly GPU path (home preview). */
  lowPower?: boolean;
  /** Cap render loop FPS (home preview). */
  maxFPS?: number;
  interactive?: boolean;
};

export async function createBoardApp(
  host: HTMLElement,
  options?: CreateBoardAppOptions
): Promise<BoardApp> {
  const app = new Application();
  const maxRes = options?.maxResolution ?? 2;
  const dpr = Math.min(window.devicePixelRatio || 1, maxRes);
  let interactive = options?.interactive !== false;

  await app.init({
    width: Math.max(1, host.clientWidth),
    height: Math.max(1, host.clientHeight),
    backgroundAlpha: 0,
    antialias: options?.antialias ?? true,
    resolution: dpr,
    autoDensity: true,
    preference: 'webgl',
    ...(options?.lowPower ? { powerPreference: 'low-power' as const } : {}),
  });

  if (options?.maxFPS) {
    app.ticker.maxFPS = options.maxFPS;
  }

  host.appendChild(app.canvas);
  app.canvas.style.display = 'block';
  app.canvas.style.width = '100%';
  app.canvas.style.height = '100%';

  const applyInteractive = (next: boolean) => {
    interactive = next;
    app.canvas.style.touchAction = next ? 'none' : 'auto';
    app.canvas.style.pointerEvents = next ? 'auto' : 'none';
    app.stage.eventMode = next ? 'static' : 'none';
    app.stage.hitArea = next ? app.screen : null;
  };

  applyInteractive(interactive);

  const resize = (width: number, height: number) => {
    if (width <= 0 || height <= 0) return;
    app.renderer.resize(width, height);
    if (interactive) app.stage.hitArea = app.screen;
  };

  const destroy = () => {
    // Do not clear Pixi's global TexturePool — CanvasText teardown shares it across apps
    // and `texture: true` / releaseGlobalResources causes returnTexture crashes on navigate.
    app.destroy(
      { removeView: true, releaseGlobalResources: false },
      { children: true, texture: false, textureSource: false }
    );
  };

  return { app, destroy, resize, setInteractive: applyInteractive };
}
